/**
 * Pure function kernel for Quadratic Gallery (二次函数图鉴): quadratic and
 * linear algebra in the Cartesian plane. No DOM, no Pixi — everything works
 * on plain numbers and {x, y} points (maths coordinates, y up), so the whole
 * module is unit-testable under vitest. Every derived quantity of the four
 * exam problems (coefficients, point coordinates, ratios, sines) is computed
 * through these formulas, never hard-coded.
 */

export interface Pt {
  readonly x: number;
  readonly y: number;
}

/** Create a point. */
export function pt(x: number, y: number): Pt {
  return { x, y };
}

/** Parabola y = a x² + b x + c. */
export interface Quad {
  readonly a: number;
  readonly b: number;
  readonly c: number;
}

/** Non-vertical line y = k x + b. */
export interface Line2 {
  readonly k: number;
  readonly b: number;
}

/** Evaluate y = a x² + b x + c at x. */
export function evalQuad(q: Quad, x: number): number {
  return q.a * x * x + q.b * x + q.c;
}

/** Evaluate y = k x + b at x. */
export function evalLine(l: Line2, x: number): number {
  return l.k * x + l.b;
}

/** Discriminant b² − 4ac. */
export function discriminant(q: Quad): number {
  return q.b * q.b - 4 * q.a * q.c;
}

/** Real roots of a x² + b x + c = 0, ascending (0, 1 or 2 entries). */
export function solveQuadratic(a: number, b: number, c: number): number[] {
  const d = b * b - 4 * a * c;
  if (d < 0) {
    return [];
  }
  if (d === 0) {
    return [-b / (2 * a)];
  }
  const sq = Math.sqrt(d);
  const r1 = (-b - sq) / (2 * a);
  const r2 = (-b + sq) / (2 * a);
  return r1 < r2 ? [r1, r2] : [r2, r1];
}

/** Zeros (x-intercepts) of the parabola, ascending. */
export function zerosOf(q: Quad): number[] {
  return solveQuadratic(q.a, q.b, q.c);
}

/** Symmetry axis x = −b/(2a). */
export function axisOf(q: Quad): number {
  return -q.b / (2 * q.a);
}

/** Vertex of the parabola. */
export function vertexOf(q: Quad): Pt {
  const x = axisOf(q);
  return pt(x, evalQuad(q, x));
}

/** Expand vertex form y = a(x − h)² + k into {a, b, c}. */
export function quadFromVertex(a: number, vertex: Pt): Quad {
  return { a, b: -2 * a * vertex.x, c: a * vertex.x * vertex.x + vertex.y };
}

/** Line through two points with distinct x coordinates. */
export function lineThrough(p: Pt, q: Pt): Line2 {
  if (Math.abs(p.x - q.x) < 1e-12) {
    throw new Error("lineThrough: vertical line");
  }
  const k = (q.y - p.y) / (q.x - p.x);
  return { k, b: p.y - k * p.x };
}

/** x-intercept of a non-horizontal line. */
export function xInterceptOf(l: Line2): number {
  return -l.b / l.k;
}

/** Intersection points of a parabola and a line (0, 1 or 2). */
export function quadLineIntersect(q: Quad, l: Line2): Pt[] {
  return solveQuadratic(q.a, q.b - l.k, q.c - l.b).map((x) => pt(x, evalLine(l, x)));
}

/* ------------------------------------------------------------- vectors */

/** Vector p -> q. */
export function sub(p: Pt, q: Pt): Pt {
  return { x: q.x - p.x, y: q.y - p.y };
}

/** p + v. */
export function add(p: Pt, v: Pt): Pt {
  return { x: p.x + v.x, y: p.y + v.y };
}

/** Scalar multiple of a vector. */
export function scale(v: Pt, k: number): Pt {
  return { x: v.x * k, y: v.y * k };
}

/** Midpoint of p and q. */
export function mid(p: Pt, q: Pt): Pt {
  return { x: (p.x + q.x) / 2, y: (p.y + q.y) / 2 };
}

/** Dot product of two vectors. */
export function dot(u: Pt, v: Pt): number {
  return u.x * v.x + u.y * v.y;
}

/** Euclidean distance between two points. */
export function dist(p: Pt, q: Pt): number {
  return Math.hypot(p.x - q.x, p.y - q.y);
}

/** Unit vector from p towards q; null when p == q. */
export function unit(p: Pt, q: Pt): Pt | null {
  const len = dist(p, q);
  if (len < 1e-12) {
    return null;
  }
  return { x: (q.x - p.x) / len, y: (q.y - p.y) / len };
}

/** Rotate a vector 90° counterclockwise: (x, y) -> (-y, x). */
export function rotate90(v: Pt): Pt {
  return { x: -v.y, y: v.x };
}
